import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import {
  Briefcase,
  Users,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import FilterButton from 'src/components/common/FilterButton';
import { Button } from 'src/components/ui/button';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from 'src/components/ui/chart';
import {
  useCseDashboardMetrics,
  useCseHighPriorityRequirements,
  useAssignmentDashboardMetrics,
  useAssignmentHighPriority,
} from 'src/services/api/queries/opportunityQueries';
import { cn } from 'src/lib/utils';

// Period selector → the metrics API's own `period` query param (confirmed
// live via meta.period in the response).
const PERIODS = [
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
  { label: '90 days', value: '90d' },
  { label: 'This Year', value: '1y' },
];

const JOB_STATUS_CFG = {
  on_track: {
    label: 'On Track',
    className: 'bg-ds-success-subtle text-ds-success',
    barColor: 'var(--ds-success)',
  },
  at_risk: {
    label: 'At Risk',
    className: 'bg-ds-warning-subtle text-ds-warning',
    barColor: 'var(--ds-warning)',
  },
  delayed: {
    label: 'Delayed',
    className: 'bg-ds-danger-subtle text-ds-danger',
    barColor: 'var(--ds-danger)',
  },
};
// Any status string the backend sends that isn't one of the three above
// (new enum value, typo, etc.) falls back to this rather than crashing on
// an undefined cfg.
const DEFAULT_JOB_STATUS_CFG = {
  label: 'Unknown',
  className: 'bg-muted text-muted-foreground',
  barColor: 'var(--muted-foreground)',
};

// Scheme → {chip classes for icon/badge backgrounds, literal color for
// recharts fills}. Reuses the same hue vocabulary as
// src/lib/statusStyles.js's STATUS_SCHEMES/TONE_BUTTON_CLASSES (one shared
// palette app-wide) rather than inventing new colors here — `blue` rides on
// the real --primary token (so it reacts to theme changes automatically),
// `emerald`/`amber` ride on the ds-success/ds-warning tokens for the same
// reason; `violet`/`slate` have no dedicated ds token yet so they fall back
// to the matching plain Tailwind shade, same precedent TONE_BUTTON_CLASSES
// already set. The `fill` values are for recharts props (gradients/cells/
// bars), which need a literal CSS color string, not a Tailwind class —
// `var(--x)`/`hsl(var(--x))` resolve fine there, same technique
// matchData.js's tierColorVar already uses.
const SCHEME = {
  blue: {
    chip: 'bg-brand-bg-subtle text-primary',
    fill: 'hsl(var(--primary))',
  },
  violet: {
    chip: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
    fill: '#7C3AED',
  },
  emerald: {
    chip: 'bg-ds-success-subtle text-ds-success',
    fill: 'var(--ds-success)',
  },
  amber: {
    chip: 'bg-ds-warning-subtle text-ds-warning',
    fill: 'var(--ds-warning)',
  },
  slate: {
    chip: 'bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-300',
    fill: '#94A3B8',
  },
};

// Static per-stat-card metadata — the live value/trend are read off the
// metrics response at render time (see Dashboard's buildStatCards below).
const STAT_CONFIG = [
  {
    key: 'active-jobs',
    label: 'Active Jobs',
    suffix: '',
    icon: Briefcase,
    scheme: 'blue',
    invertTrend: false,
    getValue: (m) => m.meta?.active_jobs_count,
    getDelta: (m) => m.trends?.active_jobs_delta,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}`,
  },
  {
    key: 'candidates-submitted',
    label: 'Candidates Submitted',
    suffix: '',
    icon: Users,
    scheme: 'violet',
    invertTrend: false,
    getValue: (m) => m.summary?.candidates?.submitted,
    getDelta: (m) => m.trends?.candidates_submitted_delta_pct,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}%`,
  },
  {
    key: 'offers-extended',
    label: 'Offers Extended',
    suffix: '',
    icon: CheckCircle2,
    scheme: 'emerald',
    invertTrend: false,
    getValue: (m) => m.summary?.candidates?.offer_extended,
    getDelta: (m) => m.trends?.offers_extended_delta,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}`,
  },
];

// ─────────────────────────────────────────────────────────────────────────
// DEMO DATA-SOURCE TOGLE — per explicit request, since all six dashboard
// endpoints currently 404 on api-dev. The header toggle (Dashboard's own
// dataMode state, 'mocked' | 'real') swaps every section's data source
// between these mock objects and the real API responses without changing
// any rendering code — both branches feed the exact same STAT_CONFIG/
// ASSIGNMENT_STAT_CONFIG getValue() accessors, chart components, and
// tables, so there's exactly one code path per widget, not two.
//
// Shaped to match the REAL confirmed metrics/high-priority-requirements
// response envelope exactly (summary/meta/trends/daily_submissions/
// job_fulfillment, due_7_days/due_16_30_days/urgent_dues) — not the old
// flat mock shape from before this page was wired to real data — so this
// really is just an alternate data source, not a parallel implementation.
// ─────────────────────────────────────────────────────────────────────────
const MOCK_REQUIREMENT_METRICS = {
  summary: {
    requirements: { active_requirements: 12, closed_requirements: 4 },
    candidates: {
      submitted: 184,
      screening: 58,
      shortlisted: 42,
      pending_interview: 34,
      offer_extended: 23,
      offer_accepted: 18,
      joined: 16,
      rejected: 27,
    },
  },
  meta: { active_jobs_count: 12 },
  trends: {
    active_jobs_delta: 3,
    candidates_submitted_delta_pct: 22,
    offers_extended_delta: 5,
  },
  daily_submissions: [
    5, 7, 4, 8, 6, 9, 11, 7, 10, 8, 12, 9, 6, 8, 11, 14, 10, 8, 7, 12, 15, 11,
    9, 13, 10, 8, 14, 12, 11, 16,
  ].map((submitted, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      date: date.toISOString().slice(0, 10),
      submitted,
      shortlisted: Math.max(1, Math.round(submitted * 0.42)),
    };
  }),
  job_fulfillment: [
    {
      requirement_id: 1,
      title: 'Senior Java Developer',
      required: 5,
      filled: 4,
      status: 'on_track',
    },
    {
      requirement_id: 2,
      title: 'Risk Analyst – Basel III',
      required: 3,
      filled: 3,
      status: 'on_track',
    },
    {
      requirement_id: 3,
      title: 'DevOps Engineer',
      required: 4,
      filled: 2,
      status: 'at_risk',
    },
    {
      requirement_id: 4,
      title: 'Compliance Officer',
      required: 2,
      filled: 0,
      status: 'delayed',
    },
  ],
};

const MOCK_REQUIREMENT_PRIORITY = {
  due_7_days: { active_count: 1, total_count: 1, ids: [] },
  due_8_15_days: { active_count: 2, total_count: 2, ids: [] },
  due_16_30_days: { active_count: 3, total_count: 3, ids: [] },
  urgent_dues: [
    {
      registration_id: 'R2909260208',
      organization: 'DBB Bank',
      title: 'Portfolio Specialist - Manager',
      deadline: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10),
    },
    {
      registration_id: 'R2909260301',
      organization: 'Acme Bank',
      title: 'Compliance Officer',
      deadline: new Date(Date.now() + 9 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10),
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────
// ASSIGNMENT-LEVEL METRICS — the dashboard's second section. A requirement
// can fan out into several assignments (one per location/TA it's forwarded
// to), so this is a genuinely different unit of count than the
// requirement-level section above, not just the same numbers restated.
//
// CONFIRMED real response shape for useAssignmentDashboardMetrics (the
// endpoint itself still 404s on api-dev as of this change — not live-checked
// yet, but the shape below is what the backend team has committed to, not a
// guess/mirror of the requirement-level endpoint like before):
//   {
//     assignments: { total, active, closed, archived },
//     candidates: { requested, submitted, shortlisted },
//     pipeline: [
//       { flow_id, flow_name, state_machine_key, total,
//         statuses: [{ status, count }],
//         tabs: [{ name, order_number, is_main_tab, parent, count }] },
//       ...
//     ],
//     meta: { ta_id },
//   }
// Two things fall out of this:
//  1. `assignments`/`candidates` are FLAT objects, not nested under
//     summary/meta/trends the way the requirement-level endpoint is — no
//     trend/delta fields exist anywhere in this response, so every
//     ASSIGNMENT_STAT_CONFIG entry's getDelta returns undefined for now
//     (StatCard already hides the trend row whenever delta is null).
//  2. `pipeline[]` is a per-flow breakdown of CANDIDATE pipeline-stage tabs
//     (New/Contact/Scheduling/… — the same vocabulary AssignmentPipeline.jsx
//     itself uses), not assignment lifecycle state — it answers "how many
//     candidates are sitting in each stage, per flow", a different question
//     than "Assignment Status" (this card's own title: "lifecycle state of
//     all assignments") is asking. `assignments.{active,closed,archived}` is
//     the field that actually answers that question, flow-independent — see
//     buildAssignmentStatusSegments below. `pipeline[]` isn't surfaced
//     anywhere on this page yet.
// ─────────────────────────────────────────────────────────────────────────

// Real assignment_details.status vocabulary, confirmed live via
// TaAssignmentView.jsx's own STATUS map — used by AssignmentFulfillmentTable
// below for its per-row status chip (a 5-way breakdown: pending/in_progress/
// completed/closed/archived). Distinct from ASSIGNMENT_SUMMARY_SCHEME below,
// which matches the dashboard metrics endpoint's own coarser 3-bucket
// summary (active/closed/archived — "active" there collapses pending/
// in_progress/completed into one bucket).
const ASSIGNMENT_STATUS_SCHEME = {
  pending: { label: 'Pending', scheme: 'amber' },
  in_progress: { label: 'In Progress', scheme: 'blue' },
  completed: { label: 'Completed', scheme: 'emerald' },
  closed: { label: 'Closed', scheme: 'emerald' },
  archived: { label: 'Archived', scheme: 'slate' },
};

// The Assignment Status donut's own 3 segments, matching `assignments`'s
// real buckets exactly (`total` isn't a segment, just the sum).
const ASSIGNMENT_SUMMARY_SCHEME = {
  active: { label: 'Active', scheme: 'blue' },
  closed: { label: 'Closed', scheme: 'emerald' },
  archived: { label: 'Archived', scheme: 'slate' },
};

const ASSIGNMENT_STAT_CONFIG = [
  {
    key: 'active-assignments',
    label: 'Active Assignments',
    suffix: '',
    icon: Briefcase,
    scheme: 'blue',
    invertTrend: false,
    getValue: (m) => m.assignments?.active,
    getDelta: () => undefined,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}`,
  },
  {
    key: 'closed-assignments',
    label: 'Closed Assignments',
    suffix: '',
    icon: CheckCircle2,
    scheme: 'emerald',
    invertTrend: false,
    // No separate "completed" bucket in the real summary (only
    // total/active/closed/archived) — `closed` is the closest real field to
    // what this card used to call "completed".
    getValue: (m) => m.assignments?.closed,
    getDelta: () => undefined,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}`,
  },
  {
    key: 'candidates-submitted-assignments',
    label: 'Candidates Submitted',
    suffix: '',
    icon: Users,
    scheme: 'violet',
    invertTrend: false,
    getValue: (m) => m.candidates?.submitted,
    getDelta: () => undefined,
    formatDelta: (d) => `${d > 0 ? '+' : ''}${d}%`,
  },
];

// Shaped to match the CONFIRMED real envelope exactly (see the comment
// block above) so mocked and real mode share the same ASSIGNMENT_STAT_CONFIG
// accessors and buildAssignmentStatusSegments below — one rendering path,
// not two. Values are demo-friendly non-zero numbers, unlike the real sample
// response (which came back all-zero on a test account).
const MOCK_ASSIGNMENT_METRICS = {
  assignments: { total: 13, active: 7, closed: 4, archived: 2 },
  candidates: { requested: 46, submitted: 12, shortlisted: 5 },
  pipeline: [
    {
      flow_id: 4,
      flow_name: 'Entry Level',
      state_machine_key: 'default',
      total: 6,
      statuses: [],
      tabs: [
        { name: 'New', order_number: 1, is_main_tab: true, count: 2 },
        { name: 'Pending', order_number: 2, is_main_tab: true, count: 1 },
        { name: 'In Progress', order_number: 3, is_main_tab: true, count: 2 },
        { name: 'Passed', order_number: 4, is_main_tab: true, count: 1 },
      ],
    },
    {
      flow_id: 5,
      flow_name: 'Senior Level',
      state_machine_key: 'senior_level_flow',
      total: 6,
      statuses: [],
      tabs: [
        { name: 'New', order_number: 1, is_main_tab: true, count: 1 },
        { name: 'Contact', order_number: 2, is_main_tab: true, count: 2 },
        { name: 'Interview', order_number: 4, is_main_tab: true, count: 2 },
        { name: 'Shortlisted', order_number: 6, is_main_tab: true, count: 1 },
      ],
    },
  ],
  meta: { ta_id: 1 },
};

// No field in the confirmed response maps to a daily created/completed
// time-series at all. Only rendered in Mocked mode — Real mode shows
// AssignmentActivityChart's own empty state instead of ever substituting
// this, so there's no silent fake-data path once dataMode is 'real'.
const MOCK_ASSIGNMENT_ACTIVITY = [
  3, 4, 2, 5, 3, 6, 4, 3, 5, 4, 6, 5, 3, 4, 5, 7, 5, 4, 3, 6, 7, 5, 4, 6, 5, 4,
  6, 5, 5, 7,
].map((created, i) => ({
  day: i + 1,
  created,
  completed: Math.max(0, Math.round(created * 0.55)),
}));

// Reads straight off the confirmed `assignments` summary object
// ({ total, active, closed, archived }) — flow-independent, matching this
// card's own "lifecycle state of all assignments" subtitle. Missing/absent
// fields default to 0, and StatusDonut already renders a clean neutral ring
// + a zeroed legend when every segment is 0 (same graceful empty-state the
// requirement-level Candidate Status donut relies on), so no separate mock
// fallback constant is needed here the way the old per-flow version required.
const buildAssignmentStatusSegments = (assignmentsSummary) =>
  Object.entries(ASSIGNMENT_SUMMARY_SCHEME).map(([key, cfg]) => ({
    name: cfg.label,
    value: assignmentsSummary?.[key] || 0,
    scheme: cfg.scheme,
  }));

// No field in the confirmed response maps to a per-assignment fulfillment
// list either. Only rendered in Mocked mode — Real mode shows
// AssignmentFulfillmentTable's own existing empty state (same "no active
// assignments" message the table already had) instead of ever substituting
// this.
const MOCK_ASSIGNMENT_FULFILLMENT = [
  {
    id: 1,
    title: 'Portfolio Specialist - Manager',
    location: 'Bengaluru',
    ta: 'Kiran Kumar',
    required: 30,
    filled: 11,
    status: 'in_progress',
  },
  {
    id: 2,
    title: 'Senior Java Developer',
    location: 'Hyderabad',
    ta: 'Vyshak K',
    required: 5,
    filled: 4,
    status: 'in_progress',
  },
  {
    id: 3,
    title: 'Risk Analyst – Basel III',
    location: 'Mumbai',
    ta: 'Christo Joseph',
    required: 3,
    filled: 3,
    status: 'completed',
  },
  {
    id: 4,
    title: 'Compliance Officer',
    location: 'Bengaluru',
    ta: 'Sanoop M',
    required: 2,
    filled: 0,
    status: 'pending',
  },
];

// Assignment-level counterpart of MOCK_REQUIREMENT_PRIORITY. CONFIRMED real
// shape for useAssignmentHighPriority:
//   { total_active_assignments, due_7_days: { count, ids, overdue_count },
//     due_8_15_days: { count, ids }, due_16_30_days: { count, ids },
//     urgent_dues: [] }
// Each due-bucket's own count field is `count`, NOT `active_count` the way
// the requirement-level endpoint's is — the two sibling endpoints turned out
// not to share one envelope after all, despite being mirrored off each
// other earlier. UrgentDuesCard now takes a `countKey` prop so each call
// site reads its own API's real field name directly instead of forcing a
// fake shared shape. `urgent_dues` items are STILL unconfirmed — the real
// sample came back an empty array, so there's nothing to verify their field
// names against yet; the item fields below (assignment_id/organization/
// title/deadline) stay a best-guess, same as before.
const MOCK_ASSIGNMENT_PRIORITY = {
  total_active_assignments: 4,
  due_7_days: { count: 1, ids: [], overdue_count: 0 },
  due_8_15_days: { count: 1, ids: [] },
  due_16_30_days: { count: 2, ids: [] },
  urgent_dues: [
    {
      assignment_id: 'A2909260112',
      organization: 'Bengaluru · Kiran Kumar',
      title: 'Portfolio Specialist - Manager',
      deadline: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10),
    },
    {
      assignment_id: 'A2909260198',
      organization: 'Hyderabad · Vyshak K',
      title: 'Senior Java Developer',
      deadline: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10),
    },
  ],
};

// Counts up from 0 to `target` once per target change — the only animation
// this page keeps from the original reference design; everything else
// leans on the plain `animate-in`/`fade-in` utilities (tailwindcss-animate,
// already used across this app's dialogs/popovers) instead of a hand-rolled
// keyframes block. `target` can legitimately be null (a metric the backend
// hasn't populated yet) — callers gate rendering on that themselves rather
// than animating toward 0, see StatCard below.
const useCountUp = (target, duration = 600) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (target == null) return undefined;
    let raf;
    let start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const t = Math.min((ts - start) / duration, 1);
      const eased = 1 - (1 - t) ** 3;
      setCount(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return count;
};

const DashboardCard = ({ className, children }) => (
  <div
    className={cn(
      'bg-card rounded-xl border border-border shadow-[0_1px_3px_rgba(0,0,0,.04)]',
      className
    )}
  >
    {children}
  </div>
);

// Requirement-level and Assignment-level metrics used to live in two
// always-mounted collapsible sections on one long page; now they're two
// tabs (FilterButton — the same underline-tab primitive InterviewBrief.jsx's
// own "Brief / Job description" switcher uses) so only one section's worth
// of cards/charts is on screen at a time. `label` feeds the tab itself,
// `subtitle` renders under it for the active tab only (see Dashboard's own
// render below) since the tab label alone doesn't carry what the old
// section heading's subtitle used to say.
const DASHBOARD_TABS = [
  {
    value: 'requirement',
    label: 'Opportunity Overview',
    subtitle: 'Across every requirement you own',
  },
  {
    value: 'assignment',
    label: 'TA sourcing Overview',
    subtitle:
      'Across every assignment forwarded to a TA — one requirement can fan out into several',
  },
];

const ChartCardHeader = ({ title, subtitle, actions }) => (
  <div className="flex items-start justify-between gap-3 mb-4">
    <div>
      <h3 className="text-[14px] font-[650] text-foreground-strong tracking-[-0.01em]">
        {title}
      </h3>
      {subtitle && (
        <p className="text-[11.5px] text-muted-foreground mt-0.5">{subtitle}</p>
      )}
    </div>
    {actions}
  </div>
);

function StatCard({ config, metrics, delayMs }) {
  const { label, suffix, icon: Icon, scheme, invertTrend } = config;
  const value = config.getValue(metrics);
  const delta = config.getDelta(metrics);
  const hasValue = value != null;
  const hasDelta = delta != null && delta !== 0;
  const count = useCountUp(hasValue ? value : null);
  const deltaIsGood = invertTrend ? delta < 0 : delta > 0;

  return (
    <DashboardCard className="animate-in fade-in-0 slide-in-from-bottom-1 duration-500 p-[18px_20px] flex items-start gap-[14px]">
      <div
        className={cn(
          'w-10 h-10 min-w-[40px] rounded-[10px] flex items-center justify-center flex-shrink-0',
          SCHEME[scheme].chip
        )}
        style={{ animationDelay: `${delayMs}ms` }}
      >
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11.5px] font-medium text-muted-foreground tracking-[0.02em] mb-0.5">
          {label}
        </div>
        <div className="text-[26px] font-bold text-foreground-strong tracking-[-0.03em] leading-[1.2] tabular-nums">
          {hasValue ? count : '—'}
          {hasValue && suffix && (
            <span className="text-[14px] font-medium text-muted-foreground ml-1">
              {suffix}
            </span>
          )}
        </div>
        {hasDelta && (
          <div
            className={cn(
              'flex items-center gap-1 mt-1 text-[11.5px] font-[550] flex-wrap',
              deltaIsGood ? 'text-ds-success' : 'text-ds-danger'
            )}
          >
            {delta > 0 ? (
              <TrendingUp className="w-[13px] h-[13px]" strokeWidth={2.2} />
            ) : (
              <TrendingDown className="w-[13px] h-[13px]" strokeWidth={2.2} />
            )}
            <span>{config.formatDelta(delta)}</span>
            <span className="text-muted-foreground font-normal text-[11px]">
              vs previous period
            </span>
          </div>
        )}
      </div>
    </DashboardCard>
  );
}

function SubmissionsAreaChart({ data, periodLabel }) {
  const chartConfig = {
    submitted: { label: 'Submitted', color: 'hsl(var(--primary))' },
    shortlisted: { label: 'Shortlisted', color: 'var(--ds-success)' },
  };
  // Thin the x-axis ticks out so they don't collide — show roughly 5 labels
  // regardless of how many days are in the selected period (7/30/90/365).
  const tickEvery = Math.max(1, Math.ceil(data.length / 5));

  return (
    <DashboardCard className="p-[20px_22px_16px] animate-in fade-in-0 duration-500">
      <ChartCardHeader
        title="Candidate Submissions"
        subtitle={`Profiles submitted over ${periodLabel}`}
        actions={
          <div className="flex items-center gap-[14px] flex-shrink-0">
            <div className="flex items-center gap-[5px] text-[11px] font-medium text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Submitted
            </div>
            <div className="flex items-center gap-[5px] text-[11px] font-medium text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-ds-success" />
              Shortlisted
            </div>
          </div>
        }
      />
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[200px] w-full"
      >
        <AreaChart
          data={data}
          margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
        >
          <defs>
            <linearGradient id="dashGradSubmitted" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor="hsl(var(--primary))"
                stopOpacity={0.2}
              />
              <stop
                offset="100%"
                stopColor="hsl(var(--primary))"
                stopOpacity={0.01}
              />
            </linearGradient>
            <linearGradient
              id="dashGradShortlisted"
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="0%"
                stopColor="var(--ds-success)"
                stopOpacity={0.18}
              />
              <stop
                offset="100%"
                stopColor="var(--ds-success)"
                stopOpacity={0.01}
              />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickLine={false}
            axisLine={false}
            fontSize={10}
            tickFormatter={(value, index) =>
              index % tickEvery === 0 ? format(new Date(value), 'd MMM') : ''
            }
          />
          <YAxis tickLine={false} axisLine={false} fontSize={10} width={28} />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(value) =>
                  format(new Date(value), 'd MMM yyyy')
                }
              />
            }
          />
          <Area
            type="monotone"
            dataKey="submitted"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#dashGradSubmitted)"
            dot={false}
          />
          <Area
            type="monotone"
            dataKey="shortlisted"
            stroke="var(--ds-success)"
            strokeWidth={2}
            fill="url(#dashGradShortlisted)"
            dot={false}
          />
        </AreaChart>
      </ChartContainer>
    </DashboardCard>
  );
}

function StatusDonut({ title, subtitle, segments, actions }) {
  const total = segments.reduce((s, d) => s + d.value, 0);
  // Pie needs at least one positive-value slice to render sensibly — an
  // all-zero pipeline (no candidates in any status yet) falls back to one
  // flat neutral ring instead of a broken/invisible chart. The legend below
  // still lists every real segment (all reading 0), so nothing's hidden.
  const pieData =
    total > 0 ? segments : [{ name: 'No data', value: 1, scheme: 'slate' }];
  const chartConfig = Object.fromEntries(
    segments.map((d) => [
      d.name,
      { label: d.name, color: SCHEME[d.scheme].fill },
    ])
  );
  // Hovering a slice highlights its legend row (and vice versa) instead of a
  // tooltip bubble — per explicit feedback, a floating tooltip over the
  // chart reads worse than just lighting up the row that already names the
  // segment. `null` means nothing hovered, so every slice/row sits at its
  // own resting opacity/weight.
  const [hoverIndex, setHoverIndex] = useState(null);

  return (
    <DashboardCard className="p-[20px_22px_16px] animate-in fade-in-0 duration-500">
      <ChartCardHeader title={title} subtitle={subtitle} actions={actions} />
      <div className="flex items-center gap-6">
        <div
          className="relative flex-shrink-0"
          style={{ width: 160, height: 160 }}
        >
          <ChartContainer
            config={chartConfig}
            className="aspect-square h-[160px] w-[160px]"
          >
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={42}
                outerRadius={64}
                paddingAngle={total > 0 ? 1 : 0}
                dataKey="value"
                nameKey="name"
                startAngle={90}
                endAngle={-270}
                onMouseEnter={
                  total > 0 ? (_, index) => setHoverIndex(index) : undefined
                }
                onMouseLeave={total > 0 ? () => setHoverIndex(null) : undefined}
              >
                {pieData.map((entry, i) => (
                  <Cell
                    key={entry.name}
                    fill={SCHEME[entry.scheme].fill}
                    opacity={
                      total === 0 || hoverIndex === null || hoverIndex === i
                        ? 0.88
                        : 0.3
                    }
                    style={{
                      transition: 'opacity 0.25s ease',
                      cursor: total > 0 ? 'pointer' : 'default',
                    }}
                  />
                ))}
              </Pie>
            </PieChart>
          </ChartContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-[20px] font-bold text-foreground-strong tracking-[-0.03em]">
              {total}
            </span>
            <span className="text-[10px] font-medium text-muted-foreground">
              Total
            </span>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {segments.map((d, i) => {
            const isHovered = hoverIndex === i;
            return (
              <div
                key={d.name}
                onMouseEnter={() => total > 0 && setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex(null)}
                className="flex items-center gap-[10px] py-[7px] px-2 -mx-2 rounded-[8px] border-b border-border last:border-b-0 transition-[background-color,transform] duration-200 ease-out cursor-default"
                style={{
                  backgroundColor: isHovered
                    ? `color-mix(in srgb, ${SCHEME[d.scheme].fill} 10%, transparent)`
                    : 'transparent',
                  transform: isHovered ? 'translateX(2px)' : 'none',
                }}
              >
                <span
                  className="w-[10px] h-[10px] rounded-[3px] flex-shrink-0 transition-transform duration-200 ease-out"
                  style={{
                    background: SCHEME[d.scheme].fill,
                    transform: isHovered ? 'scale(1.25)' : 'scale(1)',
                  }}
                />
                <span
                  className={cn(
                    'flex-1 text-[12.5px] truncate transition-colors duration-200',
                    isHovered
                      ? 'text-foreground-strong font-[600]'
                      : 'text-foreground-strong'
                  )}
                >
                  {d.name}
                </span>
                <span className="text-[12.5px] font-[650] text-foreground-strong tabular-nums">
                  {d.value}
                </span>
                <span className="text-[11px] font-medium text-muted-foreground tabular-nums w-8 text-right">
                  {total > 0 ? `${Math.round((d.value / total) * 100)}%` : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardCard>
  );
}

function JobFulfillmentTable({ jobs }) {
  return (
    <DashboardCard className="p-[20px_22px_16px] animate-in fade-in-0 duration-500 overflow-hidden">
      <ChartCardHeader
        title="Job Fulfillment Status"
        subtitle="Progress by active job description"
      />
      {jobs.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground py-6 text-center">
          No active job openings right now.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-[22px] px-[22px]">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {['Position', 'Required', 'Filled', 'Progress', 'Status'].map(
                  (h) => (
                    <th
                      key={h}
                      className="text-left px-3 py-2 text-[10.5px] font-[650] uppercase tracking-[0.06em] text-muted-foreground border-b border-border first:pl-0"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {jobs.map((job, idx) => {
                const pct =
                  job.required > 0
                    ? Math.round((job.filled / job.required) * 100)
                    : 0;
                const cfg =
                  JOB_STATUS_CFG[job.status] || DEFAULT_JOB_STATUS_CFG;
                const borderCls =
                  idx === jobs.length - 1 ? '' : 'border-b border-border';
                return (
                  <tr
                    key={job.requirement_id}
                    className="hover:bg-muted/60 transition-colors"
                  >
                    <td
                      className={cn(
                        'px-3 py-[11px] pl-0 text-[13px] font-[550] text-foreground-strong',
                        borderCls
                      )}
                    >
                      {job.title}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-[11px] text-[13px] font-semibold text-foreground-strong tabular-nums',
                        borderCls
                      )}
                    >
                      {job.required}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-[11px] text-[13px] font-semibold text-foreground-strong tabular-nums',
                        borderCls
                      )}
                    >
                      {job.filled}
                    </td>
                    <td
                      className={cn('px-3 py-[11px] w-[180px]', borderCls)}
                      aria-label={`${pct}% filled`}
                    >
                      <div className="h-[6px] rounded-[3px] overflow-hidden bg-border">
                        <div
                          className="h-full rounded-[3px] transition-[width] duration-700"
                          style={{ width: `${pct}%`, background: cfg.barColor }}
                        />
                      </div>
                    </td>
                    <td className={cn('px-3 py-[11px]', borderCls)}>
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-[2px] rounded-[4px] text-[10.5px] font-semibold tracking-[0.04em] uppercase',
                          cfg.className
                        )}
                      >
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  );
}

function AssignmentActivityChart({ data }) {
  const chartConfig = {
    created: { label: 'Created', color: 'hsl(var(--primary))' },
    completed: { label: 'Completed', color: 'var(--ds-success)' },
  };

  return (
    <DashboardCard className="p-[20px_22px_16px] animate-in fade-in-0 duration-500">
      <ChartCardHeader
        title="Assignment Activity"
        subtitle="Assignments created vs. completed over the period"
        actions={
          <div className="flex items-center gap-[14px] flex-shrink-0">
            <div className="flex items-center gap-[5px] text-[11px] font-medium text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-primary" />
              Created
            </div>
            <div className="flex items-center gap-[5px] text-[11px] font-medium text-muted-foreground">
              <span className="w-2 h-2 rounded-full bg-ds-success" />
              Completed
            </div>
          </div>
        }
      />
      {data.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground py-[72px] text-center">
          No assignment activity data available yet.
        </p>
      ) : (
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[200px] w-full"
        >
          <AreaChart
            data={data}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
          >
            <defs>
              <linearGradient id="dashGradCreated" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.2}
                />
                <stop
                  offset="100%"
                  stopColor="hsl(var(--primary))"
                  stopOpacity={0.01}
                />
              </linearGradient>
              <linearGradient
                id="dashGradAssignmentCompleted"
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop
                  offset="0%"
                  stopColor="var(--ds-success)"
                  stopOpacity={0.18}
                />
                <stop
                  offset="100%"
                  stopColor="var(--ds-success)"
                  stopOpacity={0.01}
                />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" />
            <XAxis
              dataKey="day"
              tickLine={false}
              axisLine={false}
              fontSize={10}
              tickFormatter={(v) => (v % 10 === 1 ? v : '')}
            />
            <YAxis tickLine={false} axisLine={false} fontSize={10} width={28} />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => `Day ${value}`}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="created"
              stroke="hsl(var(--primary))"
              strokeWidth={2}
              fill="url(#dashGradCreated)"
              dot={false}
            />
            <Area
              type="monotone"
              dataKey="completed"
              stroke="var(--ds-success)"
              strokeWidth={2}
              fill="url(#dashGradAssignmentCompleted)"
              dot={false}
            />
          </AreaChart>
        </ChartContainer>
      )}
    </DashboardCard>
  );
}

function AssignmentFulfillmentTable({ assignments }) {
  return (
    <DashboardCard className="p-[20px_22px_16px] animate-in fade-in-0 duration-500 overflow-hidden">
      <ChartCardHeader
        title="Assignment Fulfillment"
        subtitle="Progress by assignment, per TA"
      />
      {assignments.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground py-6 text-center">
          No active assignments right now.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-[22px] px-[22px]">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {[
                  'Assignment',
                  'TA',
                  'Required',
                  'Filled',
                  'Progress',
                  'Status',
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left px-3 py-2 text-[10.5px] font-[650] uppercase tracking-[0.06em] text-muted-foreground border-b border-border first:pl-0"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assignments.map((a, idx) => {
                const pct =
                  a.required > 0
                    ? Math.round((a.filled / a.required) * 100)
                    : 0;
                const cfg =
                  ASSIGNMENT_STATUS_SCHEME[a.status] ||
                  ASSIGNMENT_STATUS_SCHEME.pending;
                const toneCls = SCHEME[cfg.scheme].chip;
                const borderCls =
                  idx === assignments.length - 1
                    ? ''
                    : 'border-b border-border';
                return (
                  <tr
                    key={a.id}
                    className="hover:bg-muted/60 transition-colors"
                  >
                    <td
                      className={cn(
                        'px-3 py-[11px] pl-0 text-[13px] font-[550] text-foreground-strong',
                        borderCls
                      )}
                    >
                      {a.title}
                      <span className="text-muted-foreground font-normal">
                        {' '}
                        — {a.location}
                      </span>
                    </td>
                    <td
                      className={cn(
                        'px-3 py-[11px] text-[13px] text-foreground-strong',
                        borderCls
                      )}
                    >
                      {a.ta}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-[11px] text-[13px] font-semibold text-foreground-strong tabular-nums',
                        borderCls
                      )}
                    >
                      {a.required}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-[11px] text-[13px] font-semibold text-foreground-strong tabular-nums',
                        borderCls
                      )}
                    >
                      {a.filled}
                    </td>
                    <td
                      className={cn('px-3 py-[11px] w-[160px]', borderCls)}
                      aria-label={`${pct}% filled`}
                    >
                      <div className="h-[6px] rounded-[3px] overflow-hidden bg-border">
                        <div
                          className="h-full rounded-[3px] transition-[width] duration-700"
                          style={{
                            width: `${pct}%`,
                            background: SCHEME[cfg.scheme].fill,
                          }}
                        />
                      </div>
                    </td>
                    <td className={cn('px-3 py-[11px]', borderCls)}>
                      <span
                        className={cn(
                          'inline-flex items-center px-2 py-[2px] rounded-[4px] text-[10.5px] font-semibold tracking-[0.04em] uppercase',
                          toneCls
                        )}
                      >
                        {cfg.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashboardCard>
  );
}

// Deadline-proximity view off the high-priority-requirements endpoint — the
// old dashboard's "Urgent Dues" table, restyled to match this page's own
// DashboardCard language instead of the generic DataTable it used before.
// Generalized (title/subtitle/emptyLabel/countKey props) so the
// assignment-level equivalent (high-priority-assignments) can reuse it below
// — both endpoints share the same due_7_days/due_8_15_days/due_16_30_days +
// urgent_dues[] envelope shape, but each bucket's own count field is named
// differently per endpoint (`active_count` for requirements, `count` for
// assignments — confirmed, not a guess), hence `countKey` instead of a
// hardcoded field name.
function UrgentDuesCard({
  title,
  subtitle,
  emptyLabel,
  data,
  countKey = 'active_count',
}) {
  const dueBuckets = [
    { key: 'due_7_days', label: 'Due in 7 days', scheme: 'amber' },
    { key: 'due_8_15_days', label: 'Due in 8–15 days', scheme: 'blue' },
    { key: 'due_16_30_days', label: 'Due in 16–30 days', scheme: 'slate' },
  ];
  const urgentDues = data?.urgent_dues || [];

  return (
    <DashboardCard className="p-[20px_22px_16px] mt-4 animate-in fade-in-0 duration-500 overflow-hidden">
      <ChartCardHeader title={title} subtitle={subtitle} />
      <div className="grid grid-cols-3 gap-[10px] mb-4">
        {dueBuckets.map((bucket) => (
          <div
            key={bucket.key}
            className={cn(
              'rounded-[8px] px-3 py-2.5 text-center',
              SCHEME[bucket.scheme].chip
            )}
          >
            <div className="text-[18px] font-bold tabular-nums leading-[1.2]">
              {data?.[bucket.key]?.[countKey] ?? 0}
            </div>
            <div className="text-[10.5px] font-medium opacity-80 mt-0.5">
              {bucket.label}
            </div>
          </div>
        ))}
      </div>
      {urgentDues.length === 0 ? (
        <p className="text-[12.5px] text-muted-foreground py-2 text-center">
          {emptyLabel}
        </p>
      ) : (
        <div className="divide-y divide-border -mx-[22px] px-[22px]">
          {urgentDues.map((req, idx) => (
            <div
              // eslint-disable-next-line react/no-array-index-key -- no
              // confirmed stable id field for the assignment-level variant
              // yet; registration_id (requirement-level) is used when present.
              key={req.registration_id || req.assignment_id || idx}
              className="flex items-center justify-between gap-3 py-2.5"
            >
              <div className="min-w-0">
                <div className="text-[12.5px] font-[550] text-foreground-strong truncate">
                  {req.title}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {req.organization}
                  {req.registration_id && <> · {req.registration_id}</>}
                </div>
              </div>
              <div className="text-[11.5px] font-medium text-ds-danger flex-shrink-0 tabular-nums">
                {format(new Date(req.deadline), 'd MMM yyyy')}
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
}

// Pulled out of Dashboard's render to avoid a nested ternary (loading /
// error / loaded) — same loading/error chrome the Requirement Overview
// section's own stat-card row uses, just scoped to the assignment metrics
// query instead of the page-level one.
function renderAssignmentStatCards({
  isLoading,
  isError,
  onRetry,
  assignmentMetrics,
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-[14px] mb-[14px]">
        <DashboardCard className="col-span-2 lg:col-span-3 p-10 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </DashboardCard>
      </div>
    );
  }
  if (isError) {
    return (
      <DashboardCard className="p-10 flex flex-col items-center gap-3 text-center mb-[14px]">
        <AlertTriangle className="h-6 w-6 text-ds-danger" />
        <p className="text-[13.5px] text-foreground-strong">
          Couldn&apos;t load assignment metrics.
        </p>
        <Button variant="outline" size="sm" onClick={() => onRetry()}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          Try again
        </Button>
      </DashboardCard>
    );
  }
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-[14px] mb-[14px]">
      {ASSIGNMENT_STAT_CONFIG.map((config, i) => (
        <StatCard
          key={config.key}
          config={config}
          // The caller already resolves assignmentMetrics to the mock
          // constant in Mocked mode (see Dashboard's own isReal ternary) —
          // falling back to the mock constant here too used to mean that if
          // the real API ever responded with an empty/null `data` while in
          // Real mode, this would silently substitute fake numbers with no
          // "Demo data" indicator anywhere. `{}` instead makes every
          // getValue() accessor below resolve to undefined, which StatCard
          // already renders as a plain "—".
          metrics={assignmentMetrics || {}}
          delayMs={i * 60}
        />
      ))}
    </div>
  );
}

function Dashboard() {
  const [period, setPeriod] = useState('30d');

  // Demo data-source toggle — all six dashboard endpoints currently 404 on
  // api-dev (see the MOCK_* constants' own comments above), so this lets the
  // team demo the finished UI off mock data while the backend catches up,
  // without firing requests that'll just fail. Defaults to 'mocked' for
  // exactly that reason. Flipping to 'real' both switches the rendered data
  // AND enables the underlying queries — a query stays disabled, not just
  // ignored, whenever its data isn't the one being shown.
  const [dataMode, setDataMode] = useState('mocked');
  const isReal = dataMode === 'real';

  const [activeTab, setActiveTab] = useState('requirement');

  const {
    data: metricsResponse,
    isLoading: isMetricsLoading,
    isError: isMetricsError,
    refetch,
  } = useCseDashboardMetrics(period, isReal);
  const { data: priorityResponse } = useCseHighPriorityRequirements(isReal);

  const {
    data: assignmentMetricsResponse,
    isLoading: isAssignmentMetricsLoadingReal,
    isError: isAssignmentMetricsErrorReal,
    refetch: refetchAssignmentMetrics,
  } = useAssignmentDashboardMetrics(period, isReal);
  const { data: assignmentPriorityResponse } =
    useAssignmentHighPriority(isReal);

  const metrics = isReal ? metricsResponse?.data : MOCK_REQUIREMENT_METRICS;
  const priority = isReal ? priorityResponse?.data : MOCK_REQUIREMENT_PRIORITY;
  const assignmentMetrics = isReal
    ? assignmentMetricsResponse?.data
    : MOCK_ASSIGNMENT_METRICS;
  const assignmentPriority = isReal
    ? assignmentPriorityResponse?.data
    : MOCK_ASSIGNMENT_PRIORITY;
  const assignmentStatusSegments = buildAssignmentStatusSegments(
    assignmentMetrics?.assignments
  );
  const isLoading = isReal && isMetricsLoading;
  const isError = isReal && isMetricsError;
  const isAssignmentMetricsLoading = isReal && isAssignmentMetricsLoadingReal;
  const isAssignmentMetricsError = isReal && isAssignmentMetricsErrorReal;
  const candidates = metrics?.summary?.candidates || {};
  const periodLabel =
    PERIODS.find((p) => p.value === period)?.label.toLowerCase() ||
    'this period';

  const donutSegments = [
    { name: 'Under Review', value: candidates.screening || 0, scheme: 'blue' },
    {
      name: 'Shortlisted',
      value: candidates.shortlisted || 0,
      scheme: 'violet',
    },
    {
      name: 'Interview Scheduled',
      value: candidates.pending_interview || 0,
      scheme: 'amber',
    },
    {
      name: 'Offer Stage',
      value: candidates.offer_extended || 0,
      scheme: 'emerald',
    },
    { name: 'Rejected', value: candidates.rejected || 0, scheme: 'slate' },
  ];

  const periodSelector = (
    <div className="flex items-center rounded-lg border border-border overflow-hidden">
      {PERIODS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => setPeriod(p.value)}
          className={cn(
            'px-[14px] py-[7px] text-[12px] font-[550] transition-colors border-r border-border last:border-r-0',
            period === p.value
              ? 'text-primary bg-brand-bg-subtle'
              : 'text-muted-foreground hover:text-foreground-strong hover:bg-muted'
          )}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  // Real/Mocked data-source switch — see the `dataMode` comment above for
  // why this exists. A chunky pill toggle (blue track + sliding white knob)
  // per the reference design, rather than the segmented-pill style
  // periodSelector uses — this is a single on/off switch, not a multi-value
  // picker, so it reads more like a real toggle than a button group. The
  // active mode's label sits in the track on the side the knob has cleared,
  // same convention the reference toggle's own "OFF" label uses.
  const dataModeSwitch = (
    <button
      type="button"
      role="switch"
      aria-checked={isReal}
      aria-label="Toggle between real and mocked data"
      onClick={() => setDataMode(isReal ? 'mocked' : 'real')}
      className={cn(
        'relative inline-flex h-5 w-16 shrink-0 items-center rounded-full px-1',
        'transition-colors duration-200 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/[0.25]',
        isReal ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-700'
      )}
    >
      <span
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center text-[8.5px] font-bold tracking-[0.01em] text-white transition-opacity duration-150',
          isReal ? 'justify-start pl-1.5' : 'justify-end pr-1.5'
        )}
      >
        {isReal ? 'Real' : 'Mocked'}
      </span>
      <span
        className={cn(
          'z-10 h-3.5 w-3.5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.35)] transition-transform duration-200',
          isReal ? 'translate-x-[42px]' : 'translate-x-0'
        )}
      />
    </button>
  );

  return (
    <div>
      <PageHeader
        title="Dashboard"
        actions={
          <div className="flex items-center gap-[10px]">
            {periodSelector}
            {dataModeSwitch}
          </div>
        }
      />
      <PageContainer className="max-w-[1100px]">
        {isLoading && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground py-24">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-[13.5px]">Loading dashboard…</span>
          </div>
        )}

        {isError && (
          <DashboardCard className="p-10 flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="h-6 w-6 text-ds-danger" />
            <p className="text-[13.5px] text-foreground-strong">
              Couldn&apos;t load dashboard metrics.
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Try again
            </Button>
          </DashboardCard>
        )}

        {!isLoading && !isError && metrics && (
          <>
            <div className="mb-5">
              <FilterButton
                filters={DASHBOARD_TABS}
                defaultActiveFilter={activeTab}
                onFilterChange={setActiveTab}
              />
            </div>

            {activeTab === 'requirement' && (
              <div
                key="requirement"
                className="animate-in fade-in-0 duration-300"
              >
                <p className="text-[12px] text-muted-foreground mb-3 -mt-1">
                  {DASHBOARD_TABS[0].subtitle}
                </p>

                <div className="grid grid-cols-2 lg:grid-cols-3 gap-[14px] mb-[14px]">
                  {STAT_CONFIG.map((config, i) => (
                    <StatCard
                      key={config.key}
                      config={config}
                      metrics={metrics}
                      delayMs={i * 60}
                    />
                  ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[14px] mb-[14px]">
                  <SubmissionsAreaChart
                    data={metrics.daily_submissions || []}
                    periodLabel={periodLabel}
                  />
                  <StatusDonut
                    title="Candidate Status"
                    subtitle="Current status of all candidates in pipeline"
                    segments={donutSegments}
                  />
                </div>

                <div className="mb-[14px]">
                  <UrgentDuesCard
                    title="Urgent Dues"
                    subtitle="Requirements closing soon, by deadline window"
                    emptyLabel="No urgent deadlines right now."
                    data={priority}
                  />
                </div>

                <div className="mb-[14px]">
                  <JobFulfillmentTable jobs={metrics.job_fulfillment || []} />
                </div>
              </div>
            )}

            {/* ── Assignment-level metrics — real data via
                useAssignmentDashboardMetrics/useAssignmentHighPriority. The
                response shape is CONFIRMED by the backend team (see the
                comment above ASSIGNMENT_STAT_CONFIG) and only has
                assignments/candidates/pipeline/meta — no daily-activity
                time-series and no per-assignment fulfillment list. In Real
                mode those two widgets below show their own empty state
                instead of ever substituting MOCK_ASSIGNMENT_ACTIVITY/
                MOCK_ASSIGNMENT_FULFILLMENT — those constants are only used
                in Mocked mode now, never as a silent real-mode fallback (see
                each widget's own data prop below). Loading/error state is
                scoped to just this section (its own
                isAssignmentMetricsLoading/Error), not the page-level ones
                above, so a slow/broken assignment endpoint never blocks the
                other tab's data from loading in the background. The
                Assignment Status donut is derived directly from this same
                response's `assignments` summary (see
                buildAssignmentStatusSegments), not a separate flow-picker
                endpoint. ── */}
            {activeTab === 'assignment' && (
              <div
                key="assignment"
                className="animate-in fade-in-0 duration-300"
              >
                <p className="text-[12px] text-muted-foreground mb-3 -mt-1">
                  {DASHBOARD_TABS[1].subtitle}
                </p>

                {renderAssignmentStatCards({
                  isLoading: isAssignmentMetricsLoading,
                  isError: isAssignmentMetricsError,
                  onRetry: refetchAssignmentMetrics,
                  assignmentMetrics,
                })}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-[14px] mb-[14px]">
                  <AssignmentActivityChart
                    data={
                      isReal
                        ? assignmentMetrics?.daily_activity || []
                        : MOCK_ASSIGNMENT_ACTIVITY
                    }
                  />
                  <StatusDonut
                    title="Assignment Status"
                    subtitle="Current lifecycle state of all assignments"
                    segments={assignmentStatusSegments}
                  />
                </div>

                <AssignmentFulfillmentTable
                  assignments={
                    isReal
                      ? assignmentMetrics?.fulfillment || []
                      : MOCK_ASSIGNMENT_FULFILLMENT
                  }
                />

                <UrgentDuesCard
                  title="Urgent Assignments"
                  subtitle="Assignments closing soon, by deadline window"
                  emptyLabel="No urgent assignment deadlines right now."
                  data={assignmentPriority}
                  countKey="count"
                />
              </div>
            )}
          </>
        )}
      </PageContainer>
    </div>
  );
}

export default Dashboard;
